import { type ReactNode } from "react";
import { Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { AuditLogCustomWindow, AuditLogPeriod } from "./auditLogPresentation";
import type { AuditLogFacets } from "src/api/client-api/auditLog/auditLogSchemas";
import { AuditLogQueryInput } from "./AuditLogQueryInput";
import { AuditLogWindowPicker } from "./AuditLogWindowPicker";
import { type ParsedAuditLogQuery } from "./auditLogQuery";

type AuditLogQueryBarProps = {
    tokens: string[];
    onTokensChange: (tokens: string[]) => void;
    facets: AuditLogFacets | undefined;
    period: AuditLogPeriod;
    onPeriodChange: (period: AuditLogPeriod) => void;
    parsed: ParsedAuditLogQuery;
    isCompact: boolean;
    trailing?: ReactNode;
    appliedWindow: AuditLogCustomWindow;
    customWindow: AuditLogCustomWindow;
    onCustomWindowChange: (window: AuditLogCustomWindow) => void;
    onApplyCustomWindow: () => void;
};

export const AuditLogQueryBar = (props: AuditLogQueryBarProps) => {
    const {
        tokens,
        onTokensChange,
        facets,
        period,
        onPeriodChange,
        parsed,
        isCompact,
        trailing,
        appliedWindow,
        customWindow,
        onCustomWindowChange,
        onApplyCustomWindow,
    } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    const warnings = [
        ...new Set([
            ...parsed.unknownFields.map((field) =>
                t("settings.audit_log.query.unknown_field", "Unknown field: {{field}}", { field }),
            ),
            ...parsed.unknownValues.map((field) =>
                t("settings.audit_log.query.unknown_value", "Unrecognized value for {{field}}", { field }),
            ),
        ]),
    ];

    return (
        <Stack gap={theme.spacing(1)}>
            <Stack
                direction={isCompact ? "column" : "row"}
                gap={theme.spacing(2)}
                alignItems={isCompact ? "stretch" : "flex-start"}
            >
                <Stack flexGrow={1} minWidth={0}>
                    <AuditLogQueryInput tokens={tokens} onTokensChange={onTokensChange} facets={facets} />
                </Stack>
                <Stack
                    direction="row"
                    gap={theme.spacing(2)}
                    width={isCompact ? "100%" : theme.spacing(34)}
                    flexShrink={0}
                >
                    <Stack flexGrow={1} minWidth={0}>
                        <AuditLogWindowPicker
                            period={period}
                            onPeriodChange={onPeriodChange}
                            appliedWindow={appliedWindow}
                            draftWindow={customWindow}
                            onDraftWindowChange={onCustomWindowChange}
                            onApply={onApplyCustomWindow}
                            isCompact={isCompact}
                        />
                    </Stack>
                    {trailing}
                </Stack>
            </Stack>

            <Typography variant="small-regular" color="text.secondary">
                {t(
                    "settings.audit_log.query.search_scope_extended",
                    "Free text searches the actor, the modified object, the owning organization and the recipient of a role. A filter is written field:value, and -field:value excludes a value — autocompletion suggests the fields and their values.",
                )}
            </Typography>

            {warnings.map((warning) => (
                <Typography key={warning} variant="small-regular" color={theme.palette.warning.dark}>
                    {warning}
                </Typography>
            ))}
        </Stack>
    );
};
